import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, FormField, Button, MenuBoxIcon, useZodForm } from '@noor/ui';
import { getApiErrorMessage } from '@noor/api';
import { toast, useFormSeed, type ModalRenderProps } from '@noor/lib';
import { useRestaurantCategories } from '@noor/eats-api';
import { useBranchProducts } from '@/entities/menu';
import { useCuisines } from '../model/use-cuisines';
import {
  buildBranchFormSchema,
  type BranchFormValues,
  type BranchProduct,
} from '../model/schema';
import { useBranchDetails } from '../model/use-branch-details';
import { useUpsertBranch } from '../model/use-upsert-branch';
import { useSyncBranchProducts } from '../model/use-sync-branch-products';
import { emptyDefaults, missingCreateBranchFields, toDefaults } from '../lib/branch-form';
import { toBranchProduct } from '../lib/branch-products';
import { PhotoField } from './photo-field';
import { ScheduleField } from './schedule-field';
import { AddressField } from './address-field';
import { WizardSteps } from './wizard-steps';
import { ProductStep } from './product-step';

/**
 * Figma «Basic» text input (840:34910): a WHITE bordered capsule — 44px tall,
 * 24px radius, 14px medium text, 16px horizontal padding.
 *
 * Every value overrides a design-system default, because `@noor/ui`'s `Input`
 * implements a different Figma field (a 48px FILLED grey pill at 16px text)
 * that this modal does not use. `h-11` is the 44px the spec's 10px padding
 * plus 6px inner pad adds up to.
 */
const FIELD =
  'h-11 rounded-3xl border-input bg-card px-4 text-sm shadow-xs focus-visible:border-input-border-focus';

const MULTI_SELECT_FIELD =
  'min-h-11 rounded-3xl border-input bg-card px-4 py-2 text-sm shadow-xs focus-within:border-input-border-focus';

/** Figma «Basic» textarea (840:35198): the same capsule, 131px tall. */
const TEXTAREA =
  'min-h-[131px] resize-none rounded-3xl border-input bg-card px-4 py-2.5 text-sm shadow-xs focus-visible:border-input-border-focus';

/**
 * Label→control gap: 6px (Figma «Text Input» stack).
 *
 * `relative pb-6` anchors the validation message (see {@link MESSAGE}) and
 * reserves its line on EVERY field, so a row of side-by-side fields keeps one
 * shared height whether or not any of them errored.
 */
const FIELD_GROUP = 'relative gap-1.5 pb-6';

/**
 * Validation text, taken OUT OF FLOW into the space {@link FIELD_GROUP}
 * reserves.
 *
 * A `FormField` is a grid, so an in-flow message is a grid ROW: an errored
 * field grew taller than its clean siblings and, stretched in a flex row,
 * dragged their inputs out of line with each other. Absolute positioning means
 * an error can never change a field's height.
 */
const MESSAGE = 'pointer-events-none absolute top-full left-0 min-h-0 px-1 empty:hidden';

/** Figma label: 14px medium, -0.28px tracking, 4px inset. */
const LABEL = 'flex items-center gap-px px-1 text-sm font-medium tracking-tight';

/** Hairline between the modal's sections (Figma 840:35012 et al). */
const DIVIDER = 'h-px shrink-0 bg-border';

/** The wizard's two steps, in order (Figma 1066:11135 → 1067:12534). */
const STEPS = ['details', 'products'] as const;

/** The three platform languages, in the order the design lists them. */
const LOCALES = [
  { suffix: 'Ru', tag: '(Ru)' },
  { suffix: 'Uz', tag: '(Uz)' },
  { suffix: 'En', tag: '(En)' },
] as const;

type BranchModalDesktopProps =
  | ModalRenderProps<'add-branch'>
  | ModalRenderProps<'edit-branch'>;

/**
 * Add / edit branch on DESKTOP — a TWO-STEP wizard (Figma 1066:11135 «Детали»
 * → 1067:12534 «Добавление продукта»).
 *
 * A centered 880px sheet. Step 1 is the branch's own record, paired two-per-row
 * with hairline dividers; step 2 attaches products to the branch and prices
 * them for it.
 *
 * **The branch is created when step 1 is left, not when the wizard finishes.**
 * That is forced by the API rather than chosen: every attachment route is
 * `POST /products/{id}/restaurant-products`, which addresses the branch by id,
 * and a branch that does not exist yet has none. So «Следующи» saves the
 * branch and keeps the id it minted, and «Сохранить» writes the product list
 * against it. The upside is that abandoning step 2 still leaves the merchant
 * with the branch they filled in — only its menu is missing, which step 2 of a
 * later edit can finish.
 *
 * An EDIT starts on step 1 with both steps unlocked: the branch already exists,
 * so its stocked products can be read and re-priced without saving anything
 * first.
 */
export function BranchModalDesktop({ payload, close }: BranchModalDesktopProps) {
  const { t, i18n } = useTranslation('eats');
  const editingId = payload.mode === 'edit' ? payload.branchId : undefined;
  const isEditing = editingId !== undefined;
  const upsert = useUpsertBranch();
  const syncProducts = useSyncBranchProducts();

  const [step, setStep] = useState(0);
  /**
   * The branch these products attach to: the one being edited, or — once step 1
   * has saved — the id the create minted. `undefined` means step 2 has nothing
   * to write against yet, which is exactly when it must not be reachable.
   */
  const [createdId, setCreatedId] = useState<string | undefined>(undefined);
  const branchId = editingId ?? createdId;

  /** Step 2's rows, and what the branch stocked when they were seeded. */
  const [productRows, setProductRows] = useState<BranchProduct[]>([]);
  const [seededProducts, setSeededProducts] = useState<BranchProduct[]>([]);

  // Seeds the editor with every translation plus the stored schedule in one
  // call. Idle for a create.
  const { data, dataUpdatedAt } = useBranchDetails(editingId);

  // What the branch already stocks — step 2's starting rows on an edit. Idle
  // until a branch id exists, so a create fetches nothing until step 1 saves.
  const { data: branchProducts, dataUpdatedAt: productsUpdatedAt } =
    useBranchProducts(branchId);
  const { data: restaurantCategories } = useRestaurantCategories();
  const { data: cuisines = [] } = useCuisines();
  const cuisineOptions = useMemo(
    () => cuisines.map(({ id, name }) => ({ value: id, label: name })),
    [cuisines],
  );
  const categoryOptions = useMemo(
    () =>
      (restaurantCategories?.list ?? []).map((category) => ({
        label: category.name,
        value: category.id,
        disabled: !category.is_active,
      })),
    [restaurantCategories],
  );

  // Seed the product rows from the branch's real menu, once per delivered read.
  // Both the rows AND the baseline are set here: the baseline is what the
  // submit diffs against, so it must be the server's set, never the edited one.
  useFormSeed(true, branchId, productsUpdatedAt, () => {
    const rows = (branchProducts ?? []).map(toBranchProduct);
    setProductRows(rows);
    setSeededProducts(rows);
  });

  const schema = useMemo(() => buildBranchFormSchema(t, !isEditing), [t, isEditing]);
  const form = useZodForm(schema, { defaultValues: emptyDefaults(), mode: 'onSubmit' });

  // Re-seed when the by-id read delivers a NEWER record — that is what fills in
  // the translations, photos and schedule. Keyed on `dataUpdatedAt`, which only
  // advances when a fetch resolves, so it cannot reset the form mid-typing.
  //
  // Keyed on `editingId`, NOT the wizard's `branchId`: a create mints an id
  // when step 1 saves, and keying on that would change the seed key mid-flow
  // and re-seed the form from `emptyDefaults()` — wiping everything the
  // merchant just typed the moment they stepped back from step 2.
  const { reset } = form;
  useFormSeed(true, editingId, dataUpdatedAt, () => {
    reset(
      data
        ? toDefaults(
            data.restaurant,
            data.schedule,
            i18n.language.split('-')[0] as 'ru' | 'uz' | 'en',
          )
        : emptyDefaults(),
    );
  });

  /**
   * Save the branch record and move to step 2.
   *
   * The form's `onSubmit` fires this on step 1 rather than finishing the
   * wizard, so the branch exists — and has an id — before any product is
   * attached to it. On an edit it PATCHes the profile in place; the id is
   * already known either way.
   *
   * Success does NOT close the modal here: only the toast tells the merchant
   * the record landed, and the wizard advances so they can add products.
   */
  function handleDetailsSubmit(values: BranchFormValues): void {
    upsert.mutate(
      {
        values,
        branchId,
        // What the photo slots held when the modal opened — the only way to
        // tell an untouched empty slot from one the merchant just cleared.
        stored: {
          icon: Boolean(data?.restaurant.icon),
          background: Boolean(data?.restaurant.background_image),
        },
      },
      {
        onSuccess: ({ status, branchId: savedId, photosFailed, photoError }) => {
          // A gated merchant's write is QUEUED for review rather than applied,
          // so the list won't show it yet — said plainly instead of closing
          // silently, which would read as "saved".
          if (status === 'pending') {
            toast.success(t('branches.form.pendingReview'));
          } else {
            toast.success(
              isEditing ? t('branches.form.updated') : t('branches.form.created'),
            );
          }
          // The branch saved but its photos did not. Reported AFTER the save so
          // it reads as "everything else went through", not as a failed submit.
          if (photosFailed) {
            toast.error(getApiErrorMessage(photoError, t('branches.form.photoError')));
          }

          if (savedId !== undefined) setCreatedId(savedId);

          // A MODERATED create has no branch to attach products to — the write
          // is a receipt, not a record, and no id exists until a moderator
          // approves it. Attaching is impossible, so the wizard ends here
          // rather than advancing to a step that could only fail.
          if (!isEditing && savedId === undefined) {
            close();
            return;
          }
          setStep(1);
        },
        onError: (error) =>
          toast.error(getApiErrorMessage(error, t('branches.form.error'))),
      },
    );
  }

  /** Write step 2's product list against the saved branch, then close. */
  function handleProductsSubmit(): void {
    // Guarded by the button's own `disabled`, but stated here too: without an
    // id there is nothing to attach to.
    if (branchId === undefined) return;

    syncProducts.mutate(
      { branchId, rows: productRows, seeded: seededProducts },
      {
        onSuccess: ({ failed, firstError }) => {
          // Partial failure is reported rather than swallowed: each row is a
          // separate request, so some products can land while others do not.
          if (failed > 0) {
            toast.error(
              getApiErrorMessage(
                firstError,
                t('branches.form.products.partialError', { count: failed }),
              ),
            );
          } else {
            toast.success(t('branches.form.products.saved'));
          }
          close();
        },
        onError: (error) =>
          toast.error(getApiErrorMessage(error, t('branches.form.products.error'))),
      },
    );
  }

  const title = t(isEditing ? 'common:modal.edition' : 'common:modal.creation');
  const isBusy = upsert.isPending || syncProducts.isPending;

  return (
    <Modal
      title={
        /* Figma 840:34893: the orange 48px featured tile, 16px from the title. */
        <span className="flex items-center gap-4">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-3xl bg-primary text-primary-foreground shadow-xs">
            <MenuBoxIcon className="size-6" />
          </span>
          <span className="text-[1.375rem] leading-none font-semibold tracking-tight text-foreground">
            {title}
          </span>
        </span>
      }
      description={title}
      /* `max-h` + `grid-rows-[auto_minmax(0,1fr)]` are what make the body
       * scroll instead of the modal growing: the base `DialogContent` sets no
       * height cap and lays its children out as a GRID whose rows size to
       * content, so the full field set pushed the modal past the viewport and
       * the body's `overflow-y-auto` never engaged, having no constrained
       * height to overflow. `gap-0`/`p-0` hand the padding to the sections, so
       * the header divider can span the full 880px. */
      /* Padding, the close button's inset and the header's reserved right gutter
       * all step up together at `sm`. At the phone widths in the bug report the
       * 32px inset put a 44px button half off the sheet, and the header — which
       * had no gutter of its own — ran the title straight underneath it. */
      className={
        'flex max-h-[calc(100dvh-2rem)] max-w-[55rem] flex-col gap-0 ' +
        'overflow-hidden rounded-[2rem] p-0 sm:max-h-[calc(100dvh-4rem)] sm:max-w-[55rem] ' +
        '[&>[data-slot=dialog-header]]:shrink-0 [&>[data-slot=dialog-header]]:px-5 ' +
        '[&>[data-slot=dialog-header]]:pt-5 [&>[data-slot=dialog-header]]:pb-4 ' +
        '[&>[data-slot=dialog-header]]:pr-16 ' +
        'sm:[&>[data-slot=dialog-header]]:px-8 sm:[&>[data-slot=dialog-header]]:pt-8 ' +
        'sm:[&>[data-slot=dialog-header]]:pr-20 sm:[&>[data-slot=dialog-header]]:pb-6 ' +
        '[&>[data-slot=dialog-header]]:border-b [&>[data-slot=dialog-header]]:border-border ' +
        '[&>[data-slot=dialog-header]_[data-slot=dialog-description]]:sr-only ' +
        '[&>button]:top-5 [&>button]:right-5 [&>button]:size-11 ' +
        'sm:[&>button]:top-8 sm:[&>button]:right-8'
      }
    >
      {/* `min-h-0` on both this and the scroll region below: a flex child's
       * default `min-height:auto` refuses to shrink below its content, which
       * silently defeats every ancestor's overflow. */}
      <Form
        form={form}
        onSubmit={handleDetailsSubmit}
        onInvalid={(errors) => {
          const fields = isEditing ? [] : missingCreateBranchFields(errors);
          toast.error(
            fields.length > 0
              ? t('branches.form.requiredFieldsSummary', { fields: fields.join(', ') })
              : t('branches.form.validationSummary'),
          );
        }}
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6">
          {/* The two-step indicator, 32px below the header divider. */}
          <WizardSteps
            steps={STEPS.map((id) => ({ id, label: t(`branches.form.steps.${id}`) }))}
            current={step}
            onSelect={setStep}
          />

          {/* Step 1 is HIDDEN rather than unmounted while step 2 shows: the
           * form owns its field state, and unmounting the inputs would drop
           * every value the merchant typed the moment they stepped forward. */}
          <div className={step === 0 ? 'flex flex-col gap-6' : 'hidden'}>
            {/* Row 1 — name beside address (Figma 840:35011). */}
            {/* `sm:items-start`: the address column is TALLER — the picked
             * coordinates render in-flow under its pill — and a stretched row
             * would grow the name column's grid tracks to match, pushing the
             * name label and input down out of line with the address ones.
             * Pinning both columns to the top keeps the labels and pills
             * level. Scoped to `sm`: stacked on a phone, the fields must keep
             * stretching full width. */}
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
              <FormField name="name" className={`flex-1 ${FIELD_GROUP}`}>
                <FormField.Label className={LABEL}>
                  {t('branches.form.nameLabel')}
                </FormField.Label>
                <FormField.Input
                  className={FIELD}
                  placeholder={t('branches.form.namePlaceholder')}
                />
                <FormField.Message className={MESSAGE} />
              </FormField>

              <AddressField
                label={t('branches.form.addressLabel')}
                placeholder={t('branches.form.addressPlaceholder')}
                pickLabel={t('branches.form.pickOnMap')}
                fieldClassName={FIELD}
                className={`flex-1 ${FIELD_GROUP}`}
              />
            </div>

            <FormField name="phone" className={FIELD_GROUP}>
              <FormField.Label className={LABEL}>
                {t('branches.form.phoneLabel')} <span className="text-info">*</span>
              </FormField.Label>
              <FormField.Phone
                className={FIELD}
                placeholder={t('branches.form.phonePlaceholder')}
              />
              <FormField.Message className={MESSAGE} />
            </FormField>

            <div className={DIVIDER} />

            {/* Row 2 — the two uploaders, the icon narrow beside a wide
             * background (Figma 840:35136 / 840:35135). */}
            <div className="flex flex-col gap-6 sm:flex-row">
              <PhotoField
                name="icon"
                label={t('branches.form.iconLabel')}
                uploadLabel={t('branches.form.uploadPhoto')}
                hint={t('branches.form.photoHint')}
                removeLabel={t('branches.form.removePhoto')}
                required={!isEditing}
                className="w-full sm:w-[139px] sm:shrink-0"
              />
              <PhotoField
                name="backgroundImage"
                label={t('branches.form.backgroundLabel')}
                uploadLabel={t('branches.form.uploadPhoto')}
                hint={t('branches.form.photoHint')}
                removeLabel={t('branches.form.removePhoto')}
                required={!isEditing}
                className="w-full sm:w-[378px]"
              />
            </div>

            <div className={DIVIDER} />

            <div className="flex flex-col gap-6 sm:flex-row">
              {(['minPrepTime', 'maxPrepTime'] as const).map((name) => (
                <FormField key={name} name={name} className={`flex-1 ${FIELD_GROUP}`}>
                  <FormField.Label className={LABEL}>
                    {t(
                      `branches.form.${name === 'minPrepTime' ? 'minPrepTimeLabel' : 'maxPrepTimeLabel'}`,
                    )}{' '}
                    <span className="text-info">*</span>
                  </FormField.Label>
                  <FormField.Input type="number" min={0} step={1} className={FIELD} />
                  <FormField.Message className={MESSAGE} />
                </FormField>
              ))}
            </div>

            {/* «Источник меню» and «Служба доставки» are no longer asked here.
             * Both are provisioning choices rather than things a merchant
             * edits per branch: the menu source is set by whichever integration
             * feeds the catalog, and the delivery provider is set outside the
             * panel. Neither is sent on the write — `delivery_provider` is
             * omitted outright and an empty `menu_source` is dropped, so the
             * stored values are left alone. See `lib/branch-form.ts`. */}

            {/* Row 3 — restaurant rubrics and the free-delivery threshold. */}
            {isEditing && data?.restaurant.is_popular && (
              <p className="text-sm font-medium">{t('branches.form.isPopularLabel')}</p>
            )}
            <div className="flex flex-col gap-6 sm:flex-row">
              <FormField name="cuisineIds" className={`flex-1 ${FIELD_GROUP}`}>
                <FormField.Label className={LABEL}>
                  {t('branches.form.cuisinesLabel')}
                </FormField.Label>
                <FormField.MultiSelect
                  options={cuisineOptions}
                  className={MULTI_SELECT_FIELD}
                  placeholder={t('branches.form.cuisinesLabel')}
                />
              </FormField>
              <FormField name="pickupAvailable" className={`flex-1 ${FIELD_GROUP}`}>
                <FormField.Checkbox label={t('branches.form.pickupAvailableLabel')} />
              </FormField>
            </div>
            <div className="flex flex-col gap-6 sm:flex-row">
              <FormField name="restaurantCategoryIds" className={`flex-1 ${FIELD_GROUP}`}>
                <FormField.Label className={LABEL}>
                  {t('branches.form.restaurantCategoryLabel')}
                </FormField.Label>
                <FormField.MultiSelect
                  options={categoryOptions}
                  className={MULTI_SELECT_FIELD}
                  placeholder={t('branches.form.restaurantCategoryPlaceholder')}
                  searchPlaceholder={t('branches.form.restaurantCategorySearch')}
                  emptyText={t('branches.form.restaurantCategoryEmpty')}
                  selectAllLabel={t('branches.form.restaurantCategorySelectAll')}
                />
                <FormField.Message className={MESSAGE} />
              </FormField>

              <FormField name="freeDeliveryThreshold" className={`flex-1 ${FIELD_GROUP}`}>
                <FormField.Label className={LABEL}>
                  {t('branches.form.thresholdLabel')}
                </FormField.Label>
                {/* `Price`, not a bare numeric input: the field is an amount in
                  so'm, so it wants the thousands grouping and the trailing
                  «so'm» unit the other money boxes have — without the unit the
                  merchant can't tell the currency the number is read in. */}
                <FormField.Price
                  className={FIELD}
                  placeholder={t('branches.form.thresholdPlaceholder')}
                  suffix={t('branches.form.currencySuffix')}
                />
                <FormField.Message className={MESSAGE} />
              </FormField>
            </div>

            <div className="flex flex-col gap-6 sm:flex-row">
              <FormField name="minOrderAmount" className={`flex-1 ${FIELD_GROUP}`}>
                <FormField.Label className={LABEL}>
                  {t('branches.form.minOrderLabel')}
                </FormField.Label>
                <FormField.Price
                  className={FIELD}
                  placeholder={t('branches.form.minOrderPlaceholder')}
                  suffix={t('branches.form.currencySuffix')}
                />
                <FormField.Message className={MESSAGE} />
              </FormField>
              <div className="hidden flex-1 sm:block" />
            </div>

            <div className={DIVIDER} />

            {/* Description is captured per language, each row labelled with the
             * language it holds. All three are required: a branch missing a
             * translation renders blank in that locale on the storefront.
             * Figma 840:35280 stacks them full width, 21px apart. */}
            <div className="flex flex-col gap-[21px]">
              {LOCALES.map(({ suffix, tag }) => (
                <FormField
                  key={suffix}
                  name={`description${suffix}`}
                  className={FIELD_GROUP}
                >
                  <FormField.Label className={LABEL}>
                    {t('branches.form.descriptionLabel')}
                    {/* The design marks a required field in BLUE here, not the
                     * brand orange — orange is the accent, and using it would
                     * read as an action rather than a requirement. */}
                    <span className="text-info">*</span>
                    <span className="font-normal text-muted-foreground">{tag}</span>
                  </FormField.Label>
                  <FormField.TextArea
                    className={TEXTAREA}
                    placeholder={t('branches.form.descriptionPlaceholder')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>
              ))}
            </div>

            <ScheduleField label={t('branches.form.scheduleLabel')} />
          </div>

          {/* Step 2 — «Добавление продукта» (Figma 1067:12534). */}
          <div className={step === 1 ? 'block' : 'hidden'}>
            <ProductStep rows={productRows} onChange={setProductRows} />
          </div>
        </div>

        <Form.RootError />

        {/* The action band, pinned below the scroll area and RIGHT-aligned
         * (Figma 1067:12765 / 1067:12882). Step 2 pairs a grey «Назад» with
         * the orange primary; step 1 shows the primary alone. */}
        <div className="flex shrink-0 justify-end gap-3 px-5 pt-2 pb-5 sm:px-8 sm:pb-6">
          {step === 1 && (
            <Button
              type="button"
              size="lg"
              variant="secondary"
              onClick={() => setStep(0)}
              disabled={isBusy}
              className="h-12 w-full rounded-full text-base font-semibold sm:w-[233px]"
            >
              {t('common:actions.back')}
            </Button>
          )}

          {/* Full-width on a phone, where a 233px button floating at the right
           * edge of a full-bleed sheet is both hard to hit and visually adrift. */}
          {step === 0 ? (
            /* SUBMITS the form: step 1's «Следующи» is what saves the branch,
             * so it has to run the schema's validation first. */
            <Button
              type="submit"
              size="lg"
              className="h-12 w-full rounded-full text-base font-semibold sm:w-[233px]"
              disabled={isBusy}
            >
              {t('common:actions.next')}
            </Button>
          ) : (
            /* `type="button"`: step 2 writes to the Product Service, not to the
             * branch form, so submitting the form here would re-save the
             * profile instead. */
            <Button
              type="button"
              size="lg"
              onClick={handleProductsSubmit}
              disabled={isBusy || branchId === undefined}
              className="h-12 w-full rounded-full text-base font-semibold sm:w-[233px]"
            >
              {t('common:actions.save')}
            </Button>
          )}
        </div>
      </Form>
    </Modal>
  );
}
